
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Plus, Send, CheckCircle, XCircle, Trash2, Pencil, Car, CreditCard, Receipt, DollarSign, Lock, Paperclip, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextDisplay from '@/components/RichTextDisplay';
import { SearchableSelect } from '@/components/SearchableSelect';
import { FileUpload } from '@/components/FileUpload';
import { DocumentPreviewModal } from '@/components/DocumentPreviewModal';
import { sortSections } from '@/lib/sectionSort';
import api from '@/lib/api';

const TYPES = ['Mileage', 'Toll', 'Purchase'];
const MILEAGE_RATE = 0.725;
const typeIcons = { Mileage: Car, Toll: CreditCard, Purchase: Receipt };

const statusColors = {
  Draft: 'bg-muted text-muted-foreground border-border',
  Submitted: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  Approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  Rejected: 'bg-destructive/10 text-destructive border-destructive/20',
};

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function ReimbursementsPage() {
  const { user, hasPerm } = useAuth();
  // Manager status now derived from the granular approve_reimbursements
  // permission (Admin/Owner always get it; OM toggleable; Employee=false).
  const isManager = !!hasPerm('approve_reimbursements');

  const [entries, setEntries] = useState([]);
  const [payPeriod, setPayPeriod] = useState({ start: '', end: '' });
  const [projects, setProjects] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [projectSections, setProjectSections] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState({ type: 'Mileage', date: '', project_id: '', proposal_id: '', contract_section_id: '', description: '', miles: '', amount: '', receipt_url: '', payment_source: 'employee' });

  const [approvalQueue, setApprovalQueue] = useState([]);
  const [reviewSheet, setReviewSheet] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [periodLocked, setPeriodLocked] = useState(null);
  const [previewReceipt, setPreviewReceipt] = useState(null);

  const fetchPayPeriod = useCallback(async () => {
    const r = await api.get('/pay-periods/current');
    setPayPeriod(r.data);
    return r.data;
  }, []);

  const fetchEntries = useCallback(async () => {
    if (!payPeriod.start) return;
    const r = await api.get('/reimbursements', { params: { user_id: user.id, pay_period_start: payPeriod.start } });
    setEntries(r.data);
  }, [user?.id, payPeriod.start]);

  const fetchApproval = useCallback(async () => {
    if (!isManager) return;
    try {
      const r = await api.get('/reimbursements/approval-queue');
      setApprovalQueue(r.data);
    } catch { /* best-effort fetch — silent fail */ }
  }, [isManager]);

  useEffect(() => {
    fetchPayPeriod();
    fetchApproval();
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    // Open proposals only (exclude Closed-Lost; keep Closed-Won since reimbs may
    // still be in flight against the original proposal up until the DD invoice is paid)
    api.get('/proposals').then(r => setProposals((r.data || []).filter(p => p.status !== 'Closed - Lost'))).catch(() => {});
  }, [fetchPayPeriod, fetchApproval]);

  useEffect(() => {
    if (payPeriod.start) {
      fetchEntries();
      fetchApproval();
      api.get('/payroll-periods/lock-status', { params: { pay_period_start: payPeriod.start } })
        .then(r => setPeriodLocked(r.data.locked ? r.data : null))
        .catch(() => setPeriodLocked(null));
    }
  }, [payPeriod.start, fetchEntries, fetchApproval]);

  const loadSections = async (projectId) => {
    if (!projectId || projectSections[projectId]) return;
    try {
      const r = await api.get(`/projects/${projectId}/sections`);
      setProjectSections(prev => ({ ...prev, [projectId]: r.data }));
    } catch { /* best-effort fetch — silent fail */ }
  };

  const totalAmount = useMemo(() => entries.reduce((s, e) => s + e.amount, 0), [entries]);
  const periodStatus = useMemo(() => {
    if (entries.length === 0) return 'empty';
    if (entries.every(e => e.status === 'Approved')) return 'Approved';
    if (entries.some(e => e.status === 'Submitted')) return 'Submitted';
    return 'Draft';
  }, [entries]);

  const openNew = () => {
    setEditEntry(null);
    setForm({ type: 'Mileage', date: '', project_id: '', proposal_id: '', contract_section_id: '', description: '', miles: '', amount: '', receipt_url: '', payment_source: 'employee' });
    setFormOpen(true);
  };

  const openEdit = (e) => {
    setEditEntry(e);
    setForm({
      type: e.type, date: e.date, project_id: e.project_id || '',
      proposal_id: e.proposal_id || '',
      contract_section_id: e.contract_section_id || '',
      description: e.description || '', miles: e.miles || '',
      amount: e.type !== 'Mileage' ? e.amount : '', receipt_url: e.receipt_url || '',
      receipt_file_name: e.receipt_file_name || '', receipt_file_size: e.receipt_file_size || 0,
      receipt_content_type: e.receipt_content_type || '', receipt_file_id: '',
      payment_source: e.payment_source || 'employee'
    });
    if (e.project_id) loadSections(e.project_id);
    setFormOpen(true);
  };

  const saveEntry = async () => {
    if (!form.date) return toast.error('Date is required');
    if (!form.type) return toast.error('Expense type is required');
    if (form.project_id && !form.contract_section_id) return toast.error('Please select a Section for this project expense');
    try {
      const payload = { ...form };
      if (payload.type === 'Mileage') {
        if (!payload.miles || parseFloat(payload.miles) <= 0) return toast.error('Miles required for mileage');
        payload.miles = parseFloat(payload.miles);
        delete payload.amount;
      } else {
        if (!payload.amount || parseFloat(payload.amount) <= 0) return toast.error('Amount is required');
        payload.amount = parseFloat(payload.amount);
        delete payload.miles;
      }
      if (!payload.project_id) {
        payload.project_id = null;
        payload.contract_section_id = null;
      }
      if (!payload.proposal_id) payload.proposal_id = null;
      if (payload.project_id && payload.proposal_id) {
        return toast.error('Pick a Project OR a Proposal, not both');
      }
      if (editEntry) {
        await api.put(`/reimbursements/${editEntry.id}`, payload);
        toast.success('Expense updated');
      } else {
        await api.post('/reimbursements', payload);
        toast.success('Expense added');
      }
      setFormOpen(false);
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving expense'); }
  };

  const deleteEntry = async (eid) => {
    try {
      await api.delete(`/reimbursements/${eid}`);
      toast.success('Expense deleted');
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const submitPeriod = async () => {
    const draftEntries = entries.filter(e => e.status === 'Draft');
    const draftCount = draftEntries.length;
    if (draftCount === 0) return toast.error('No draft expenses to submit');
    // Reminder (not a hard block): warn the user if any draft entry lacks a
    // receipt. Mileage doesn't require receipts since it's per-mile.
    const missingReceipt = draftEntries.filter(
      e => e.type !== 'Mileage' && !e.receipt_url
    );
    if (missingReceipt.length > 0) {
      const proceed = window.confirm(
        `${missingReceipt.length} of ${draftCount} expenses don't have a receipt attached. Submit anyway? ` +
        `(Approvers usually need receipts to verify non-mileage expenses.)`
      );
      if (!proceed) return;
    }
    try {
      const r = await api.post('/reimbursements/submit', { pay_period_start: payPeriod.start });
      toast.success(`${r.data.submitted} expenses submitted`);
      fetchEntries();
      fetchApproval();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const approveAll = async (item) => {
    try {
      await api.post('/reimbursements/approve-all', { user_id: item.user_id, pay_period_start: item.pay_period_start });
      toast.success(`Approved ${item.user_name}'s reimbursements`);
      setReviewSheet(null);
      fetchApproval();
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const rejectAll = async (item) => {
    if (!rejectionNote.trim()) return toast.error('Rejection note required');
    try {
      await api.post('/reimbursements/reject-all', { user_id: item.user_id, pay_period_start: item.pay_period_start, rejection_note: rejectionNote });
      toast.success(`Rejected ${item.user_name}'s reimbursements`);
      setRejectionNote('');
      setReviewSheet(null);
      fetchApproval();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const mileagePreview = form.type === 'Mileage' && form.miles ? `$${(parseFloat(form.miles) * MILEAGE_RATE).toFixed(2)}` : null;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="reimbursements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-medium tracking-tight">Reimbursements</h1>
        </div>
      </div>

      <Tabs defaultValue="my-expenses">
        <TabsList>
          <TabsTrigger value="my-expenses" className="text-xs" data-testid="tab-my-expenses">
            <DollarSign className="h-3 w-3 mr-1" /> My Expenses
          </TabsTrigger>
          {isManager && (
            <TabsTrigger value="approval" className="text-xs" data-testid="tab-reimb-approval">
              <CheckCircle className="h-3 w-3 mr-1" /> Approval Queue
              {approvalQueue.length > 0 && (
                <Badge className="ml-1 h-4 min-w-4 rounded-full bg-primary text-xs px-1">{approvalQueue.length}</Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* MY EXPENSES */}
        <TabsContent value="my-expenses" className="mt-4 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-sm font-medium">
              Pay Period: {formatShortDate(payPeriod.start)} - {formatShortDate(payPeriod.end)}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Badge className={statusColors[periodStatus] || 'bg-muted text-muted-foreground'}>
                {periodStatus === 'empty' ? 'No Expenses' : periodStatus}
              </Badge>
              <span className="text-sm font-heading font-bold tabular-nums">${totalAmount.toFixed(2)} total</span>
            </div>
          </div>

          {/* Payroll Period Lock Banner */}
          {periodLocked && (
            <div className="flex items-center gap-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/20" data-testid="payroll-lock-banner">
              <Lock className="h-5 w-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-400">Payroll Period Locked</p>
                <p className="text-xs text-amber-400/80">
                  This pay period is in <span className="font-semibold">{periodLocked.stage}</span> status. New reimbursement entries cannot be created or submitted until the period is reopened.
                </p>
              </div>
            </div>
          )}

          {/* Action buttons at top */}
          <div className="flex items-center gap-2">
            <Button onClick={openNew} className="bg-primary text-primary-foreground" disabled={!!periodLocked} data-testid="add-expense-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Expense
            </Button>
            {entries.some(e => e.status === 'Draft') && !periodLocked && (
              <Button onClick={submitPeriod} variant="outline" className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10" data-testid="submit-expenses-btn">
                <Send className="h-4 w-4 mr-1" /> Submit Expenses
              </Button>
            )}
          </div>

          {/* Entries sorted: Draft > Submitted > Approved */}
          {entries.length === 0 ? (
            <Card className="border-border bg-background shadow-none">
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                No expenses for this pay period. Click &quot;Add Expense&quot; to get started.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {[...entries].sort((a, b) => {
                const order = { Draft: 0, Submitted: 1, Approved: 2, Rejected: 0 };
                return (order[a.status] ?? 3) - (order[b.status] ?? 3);
              }).map(e => {
                const Icon = typeIcons[e.type] || Receipt;
                return (
                  <Card key={e.id} className="border-border bg-background shadow-none">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Icon className="h-4 w-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-muted-foreground">{formatShortDate(e.date)}</span>
                              <Badge variant="outline" className="text-xs">{e.type}</Badge>
                              {e.payment_source === 'company_card' && (
                                <Badge className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/20" data-testid={`company-card-badge-${e.id}`}>
                                  <CreditCard className="h-2.5 w-2.5 mr-0.5" /> Company Card
                                </Badge>
                              )}
                              <Badge className={`text-xs ${statusColors[e.status]}`}>{e.status}</Badge>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              <RichTextDisplay value={e.description} collapsedHeight={20} />
                              {e.type === 'Mileage' && e.miles && <span className="text-xs"> ({e.miles} mi @ ${MILEAGE_RATE}/mi)</span>}
                            </div>
                            {e.project_name && <p className="text-xs text-muted-foreground/70 truncate">{e.project_name}{e.section_name ? ` / ${e.section_name}` : ''}</p>}
                            {e.rejection_note && <p className="text-xs text-destructive mt-0.5">Rejected: {e.rejection_note}</p>}
                            {e.receipt_url && <button type="button" onClick={(ev) => { ev.stopPropagation(); setPreviewReceipt({ name: e.description || 'Receipt', file_url: e.receipt_url, content_type: e.receipt_content_type || '', file_name: e.receipt_file_name || 'Receipt' }); }} className="text-xs text-primary underline flex items-center gap-0.5 mt-0.5 hover:text-primary/80"><Eye className="h-2.5 w-2.5" /> Receipt</button>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className="text-sm font-heading font-bold text-primary tabular-nums">${e.amount.toFixed(2)}</span>
                          {e.status === 'Draft' && (
                            <>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(e)} data-testid={`edit-reimb-${e.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteEntry(e.id)} data-testid={`delete-reimb-${e.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* APPROVAL */}
        {isManager && (
          <TabsContent value="approval" className="mt-4 space-y-4">
            <h2 className="text-sm font-semibold text-muted-foreground">Pending Approval ({approvalQueue.length})</h2>
            {approvalQueue.length === 0 ? (
              <Card className="border-border bg-background shadow-none">
                <CardContent className="p-8 text-center text-sm text-muted-foreground">No reimbursements pending approval.</CardContent>
              </Card>
            ) : (
              approvalQueue.map((item, i) => (
                <Card key={i} className="border-border bg-background shadow-none cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => setReviewSheet(item)} data-testid={`reimb-approval-item-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{item.user_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Pay Period: {formatShortDate(item.pay_period_start)} - {formatShortDate(item.pay_period_end)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-heading font-bold text-primary tabular-nums">${item.total_amount.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">{item.entry_count} expenses</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* ADD/EDIT EXPENSE SHEET */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">{editEntry ? 'Edit Expense' : 'Add Expense'}</SheetTitle>
            <SheetDescription>Mileage rate: ${MILEAGE_RATE}/mile (2026)</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div>
              <Label className="text-xs text-muted-foreground">Expense Type *</Label>
              <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v, miles: '', amount: '' }))}>
                <SelectTrigger className="bg-background border-border" data-testid="expense-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Paid By *</Label>
              <Select value={form.payment_source} onValueChange={v => setForm(f => ({ ...f, payment_source: v }))}>
                <SelectTrigger className="bg-background border-border" data-testid="expense-payment-source"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="employee">Employee (reimburse via paycheck)</SelectItem>
                  <SelectItem value="company_card">Company Card (skip paycheck, expense only)</SelectItem>
                </SelectContent>
              </Select>
              {form.payment_source === 'company_card' && (
                <p className="text-xs text-amber-400 mt-1">
                  You won&apos;t be reimbursed - the company already paid. On approval this becomes a Reimbursable Expense journal entry{(form.project_id || form.proposal_id) ? ' linked to the ' + (form.proposal_id ? 'proposal, available to add to the Due Diligence invoice.' : 'project, available to add to the next invoice.') : '.'}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="bg-background border-border" data-testid="expense-date" />
            </div>
            {form.type === 'Mileage' ? (
              <div>
                <Label className="text-xs text-muted-foreground">Miles *</Label>
                <Input type="number" step="0.1" min="0" value={form.miles} onChange={e => setForm(f => ({ ...f, miles: e.target.value }))} className="bg-background border-border" placeholder="0.0" data-testid="expense-miles" />
                {mileagePreview && <p className="text-xs text-primary mt-1">Estimated: {mileagePreview}</p>}
              </div>
            ) : (
              <div>
                <Label className="text-xs text-muted-foreground">Amount ($) *</Label>
                <Input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="bg-background border-border" placeholder="0.00" data-testid="expense-amount" />
              </div>
            )}
            <div>
              <Label className="text-xs text-muted-foreground">Project (optional)</Label>
              <SearchableSelect
                value={form.project_id || ''}
                onValueChange={v => {
                  setForm(f => ({ ...f, project_id: v, proposal_id: v ? '' : f.proposal_id, contract_section_id: '' }));
                  if (v) loadSections(v);
                }}
                options={[{ value: '', label: 'No Project (Personal)' }, ...projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))]}
                placeholder="No Project (Personal)"
                searchPlaceholder="Search projects..."
                data-testid="expense-project"
              />
            </div>
            {!form.project_id && (
              <div>
                <Label className="text-xs text-muted-foreground">Or link to a Proposal (Due Diligence)</Label>
                <SearchableSelect
                  value={form.proposal_id || ''}
                  onValueChange={v => setForm(f => ({ ...f, proposal_id: v, project_id: v ? '' : f.project_id, contract_section_id: '' }))}
                  options={[{ value: '', label: 'No Proposal' }, ...proposals.map(p => {
                    const clientLabel = p.client_name || p.contact_name || '';
                    return { value: p.id, label: clientLabel ? `${p.proposal_number} · ${clientLabel}` : p.proposal_number };
                  })]}
                  placeholder="Select proposal..."
                  searchPlaceholder="Search proposals..."
                  data-testid="expense-proposal"
                />
                {form.proposal_id && (
                  <p className="text-xs text-muted-foreground mt-1">This expense becomes available to attach to the proposal&apos;s Due Diligence invoice.</p>
                )}
              </div>
            )}
            {form.project_id && (
              <div>
                <Label className="text-xs text-muted-foreground">Section *</Label>
                <Select value={form.contract_section_id || ''} onValueChange={v => setForm(f => ({ ...f, contract_section_id: v }))}>
                  <SelectTrigger className="bg-background border-border" data-testid="expense-section"><SelectValue placeholder="Select section" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {sortSections(projectSections[form.project_id] || []).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.section_number ? `${s.section_number} - ` : ''}{s.section_name}{s.source_tag && s.source_tag !== 'Original Contract' ? ` [${s.source_tag}]` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <RichTextEditor value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} placeholder="e.g. Travel to job site" collapsedHeight={80} data-testid="expense-description" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Receipt / Proof (optional)</Label>
              {form.receipt_url ? (
                <div className="flex items-center gap-2 p-2 border border-emerald-500/20 rounded-md bg-emerald-500/5 mt-1" data-testid="receipt-file-info">
                  <Paperclip className="h-4 w-4 text-emerald-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{form.receipt_file_name || 'Receipt attached'}</p>
                    {form.receipt_file_size > 0 && <p className="text-xs text-muted-foreground">{(form.receipt_file_size / 1024).toFixed(0)} KB</p>}
                  </div>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" onClick={() => { if (form.receipt_file_id) api.delete(`/upload/${form.receipt_file_id}`).catch(() => {}); setForm(f => ({ ...f, receipt_url: '', receipt_file_name: '', receipt_file_size: 0, receipt_content_type: '', receipt_file_id: '' })); }} data-testid="clear-receipt">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <FileUpload
                  onUploadComplete={(data) => setForm(f => ({ ...f, receipt_url: data.file_url, receipt_file_name: data.file_name, receipt_file_size: data.file_size, receipt_content_type: data.content_type, receipt_file_id: data.file_id }))}
                  className="mt-1"
                />
              )}
            </div>
            <Button onClick={saveEntry} className="w-full bg-primary text-primary-foreground mt-2" data-testid="save-expense-btn">
              {editEntry ? 'Update Expense' : 'Add Expense'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* REVIEW SHEET */}
      <Sheet open={!!reviewSheet} onOpenChange={v => { if (!v) setReviewSheet(null); }}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          {reviewSheet && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading">Review: {reviewSheet.user_name}</SheetTitle>
                <SheetDescription>
                  Pay Period: {formatShortDate(reviewSheet.pay_period_start)} - {formatShortDate(reviewSheet.pay_period_end)} | ${reviewSheet.total_amount.toFixed(2)} | {reviewSheet.entry_count} expenses
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-3">
                {reviewSheet.entries.map((e, i) => {
                  const Icon = typeIcons[e.type] || Receipt;
                  return (
                    <Card key={i} className="border-border bg-background shadow-none">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Icon className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono">{formatShortDate(e.date)}</span>
                              <Badge variant="outline" className="text-xs">{e.type}</Badge>
                              {e.payment_source === 'company_card' && (
                                <Badge className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/20">
                                  <CreditCard className="h-2.5 w-2.5 mr-0.5" /> Company Card
                                </Badge>
                              )}
                              <span className="text-sm font-bold text-primary tabular-nums">${e.amount.toFixed(2)}</span>
                            </div>
                            <div className="text-xs text-muted-foreground"><RichTextDisplay value={e.description} collapsedHeight={20} /></div>
                            {e.project_name && <p className="text-xs text-muted-foreground/70">{e.project_name}</p>}
                            {e.receipt_url && <button type="button" onClick={() => setPreviewReceipt({ name: e.description || 'Receipt', file_url: e.receipt_url, content_type: e.receipt_content_type || '', file_name: e.receipt_file_name || 'Receipt' })} className="text-xs text-primary underline flex items-center gap-0.5 hover:text-primary/80"><Eye className="h-2.5 w-2.5" /> View Receipt</button>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                <div className="border-t border-border pt-4 space-y-3">
                  {reviewSheet.user_id === user?.id ? (
                    <div className="text-center py-3">
                      <p className="text-sm font-medium text-amber-400">Self-Approval Not Allowed</p>
                      <p className="text-xs text-muted-foreground mt-1">You cannot approve or reject your own reimbursements. Another manager must review these.</p>
                    </div>
                  ) : (
                    <>
                      <Button onClick={() => approveAll(reviewSheet)} className="w-full bg-emerald-500 text-white hover:bg-emerald-600" data-testid="approve-reimb-btn">
                        <CheckCircle className="h-4 w-4 mr-1" /> Approve All (${reviewSheet.total_amount.toFixed(2)})
                      </Button>
                      <div className="space-y-2">
                        <ExpandableTextarea
                          value={rejectionNote} onChange={e => setRejectionNote(e.target.value)}
                          placeholder="Rejection reason (required)..."
                          className="bg-background border-border" rows={2} expandedRows={6}
                          data-testid="reimb-rejection-note"
                        />
                        <Button onClick={() => rejectAll(reviewSheet)} variant="outline" className="w-full text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="reject-reimb-btn">
                          <XCircle className="h-4 w-4 mr-1" /> Reject All
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <DocumentPreviewModal
        open={!!previewReceipt}
        onClose={() => setPreviewReceipt(null)}
        doc={previewReceipt}
      />
    </div>
  );
}
